import {
    HttpClient,
    HttpRequestData,
    HttpRequestError,
    HttpRequestMeta,
    useLoginState,
    viewRequests,
} from '@esg/framework';
import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query';
import { useLogout } from './useLogout';
import { useTranslation } from 'react-i18next';

interface HttpQueryOptions<TResponse> extends Omit<UndefinedInitialDataOptions<TResponse, Error>, 'queryKey'> {
    readonly queryKey?: unknown[];
    readonly showNotificationError?: boolean;
}

export const useHttpQuery = <TRequest, TResponse>(
    requestMeta: HttpRequestMeta<TRequest, TResponse>,
    requestData?: HttpRequestData,
    options?: HttpQueryOptions<TResponse>) => {
    const { t } = useTranslation();
    const logout = useLogout();
    const [loginInfo] = useLoginState();
    const showNotificationError = options?.showNotificationError ?? true;

    const baseUrl = requestMeta.baseUrl();

    if (!baseUrl) {
        throw new Error('Base URL is null');
    }

    const query = useQuery<TResponse, Error>({
        ...options,
        placeholderData: (previousData) => previousData,
        queryKey: [requestMeta.method, baseUrl, requestMeta.path, { ...requestData?.query }, { ...requestData?.pathData }],
        queryFn: async (): Promise<TResponse> => {
            const httpClient = new HttpClient();
            if (requestMeta.authentication === 'bearer') {
                const accessToken = loginInfo?.token;
                if (!!accessToken) {
                    httpClient.setAuthorization('bearer', accessToken);
                }
            }

            if (requestMeta.authentication === 'basic') {
                const accessKey = requestData?.header?.Authorization;
                if (!!accessKey) {
                    httpClient.setAuthorization('basic', accessKey);
                }
            }

            try {
                return await httpClient.send<TResponse>({
                    ...requestMeta,
                    ...requestData,
                });
            } catch (error) {
                if (error instanceof Error && error.name === 'HttpRequestError') {
                    const requestError = error as HttpRequestError;

                    if (requestError.statusCode === 401) {
                        logout();
                        location.href = '/error/unauthorized';
                    }
                }

                if (error instanceof Error && error.message === 'Failed to fetch') {
                    viewRequests.showNotification.send({
                        type: 'error',
                        title: 'Network Error',
                        message: 'Please check your internet connection and try again.',
                    });
                    throw error;
                }

                if (showNotificationError) {
                    viewRequests.showNotification.send({
                        type: 'error',
                        title: (error as Error).name,
                        message: t((error as Error).message),
                    });
                }

                throw error;
            }
        },

    });

    return query;
};
